/**
 * Multi-select status filter dropdown
 * Story 198: View and filter stories in backlog
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import './StatusFilter.css';

/** Available story statuses */
const STATUSES = [
  { value: 'proposed', label: 'Proposed', color: '#6e7681' },
  { value: 'in-progress', label: 'In Progress', color: '#f0883e' },
  { value: 'qa', label: 'QA', color: '#58a6ff' },  // Story 274
  { value: 'blocked', label: 'Blocked', color: '#f85149' },
  { value: 'paused', label: 'Paused', color: '#a371f7' },
  { value: 'done', label: 'Done', color: '#3fb950' },
] as const;

interface StatusFilterProps {
  selected: string[];
  onChange: (statuses: string[]) => void;
}

export function StatusFilter({ selected, onChange }: StatusFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleStatusToggle = useCallback(
    (status: string) => {
      const newSelected = selected.includes(status)
        ? selected.filter((s) => s !== status)
        : [...selected, status];
      onChange(newSelected);
    },
    [selected, onChange]
  );

  // Story 233: Renamed from "Clear filter" to "Show all" for better UX
  const handleShowAll = useCallback(() => {
    onChange([]);
    setIsOpen(false);
  }, [onChange]);

  return (
    <div className="status-filter" ref={containerRef}>
      <button
        className={`status-filter__button ${selected.length > 0 ? 'status-filter__button--active' : ''}`}
        onClick={handleToggle}
        type="button"
        title="Filter by status"
      >
        <span>Status</span>
        <svg className="status-filter__icon" viewBox="0 0 16 16" fill="currentColor">
          <path d="M.75 3h14.5a.75.75 0 0 1 0 1.5H.75a.75.75 0 0 1 0-1.5ZM3 7.75A.75.75 0 0 1 3.75 7h8.5a.75.75 0 0 1 0 1.5h-8.5A.75.75 0 0 1 3 7.75Zm3 4a.75.75 0 0 1 .75-.75h2.5a.75.75 0 0 1 0 1.5h-2.5a.75.75 0 0 1-.75-.75Z" />
        </svg>
      </button>

      {isOpen && (
        <div className="status-filter__dropdown">
          {STATUSES.map((status) => (
            <label key={status.value} className="status-filter__option">
              <input
                type="checkbox"
                checked={selected.includes(status.value)}
                onChange={() => handleStatusToggle(status.value)}
              />
              <span
                className="status-filter__dot"
                style={{ backgroundColor: status.color }}
              />
              <span className="status-filter__label">{status.label}</span>
            </label>
          ))}
          {selected.length > 0 && (
            <>
              <div className="status-filter__divider" />
              <button
                className="status-filter__clear"
                onClick={handleShowAll}
                type="button"
              >
                Show all
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
